import React, { useEffect, useRef, useState } from 'react';
import FacebookIcon from '@mui/icons-material/Facebook';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import AuthService from '../services/AuthService';

const GRAPH_VERSION = 'v19.0';
const FACEBOOK_SCOPE = 'public_profile,email';
const GSI_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_SCOPES = 'openid email profile';
const GOOGLE_CANCELLED_MESSAGE = 'Google sign-in was cancelled.';

function GoogleMarkIcon() {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden
      sx={{ width: 18, height: 18 }}
    >
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </Box>
  );
}

function loadFacebookSdk() {
  if (window.FB) return Promise.resolve(window.FB);
  if (window.__fbSdkPromise) return window.__fbSdkPromise;
  window.__fbSdkPromise = new Promise((resolve, reject) => {
    const previous = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      if (typeof previous === 'function') previous();
      resolve(window.FB);
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => reject(new Error('Facebook SDK failed to load'));
    document.body.appendChild(script);
  });
  return window.__fbSdkPromise;
}

function loadGoogleGsi() {
  if (window.google?.accounts?.oauth2) return Promise.resolve(window.google.accounts.oauth2);
  if (window.__gsiPromise) return window.__gsiPromise;
  window.__gsiPromise = new Promise((resolve, reject) => {
    const finish = () => {
      if (window.google?.accounts?.oauth2) resolve(window.google.accounts.oauth2);
      else reject(new Error('Google Identity Services failed to initialize'));
    };
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
      if (window.google?.accounts?.oauth2) {
        finish();
        return;
      }
      existing.addEventListener('load', finish);
      existing.addEventListener('error', () => reject(new Error('Google GSI failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.onload = finish;
    script.onerror = () => reject(new Error('Google GSI failed to load'));
    document.body.appendChild(script);
  });
  return window.__gsiPromise;
}

/** GIS oauth2 error_callback: popup_closed means the user closed the consent window. */
function isPopupClosedError(err) {
  const type = String(err?.type || '').toLowerCase();
  return type === 'popup_closed' || type.includes('popup_closed');
}

function OAuthProviderButtons({ onAuthenticated, onError, onCancel }) {
  const [googleClientId, setGoogleClientId] = useState(null);
  const [facebookAppId, setFacebookAppId] = useState(null);
  const [facebookBusy, setFacebookBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const onAuthenticatedRef = useRef(onAuthenticated);
  const onErrorRef = useRef(onError);
  const onCancelRef = useRef(onCancel);
  const tokenClientRef = useRef(null);

  useEffect(() => { onAuthenticatedRef.current = onAuthenticated; }, [onAuthenticated]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);

  const reportGoogleCancel = () => {
    if (onCancelRef.current) onCancelRef.current(GOOGLE_CANCELLED_MESSAGE);
    else if (onErrorRef.current) onErrorRef.current(GOOGLE_CANCELLED_MESSAGE);
  };

  useEffect(() => {
    let cancelled = false;
    AuthService.googleClientId()
      .then((id) => { if (!cancelled) setGoogleClientId(typeof id === 'string' ? id.trim() : ''); })
      .catch(() => { if (!cancelled) setGoogleClientId(''); });
    AuthService.facebookAppId()
      .then((id) => { if (!cancelled) setFacebookAppId(typeof id === 'string' ? id.trim() : ''); })
      .catch(() => { if (!cancelled) setFacebookAppId(''); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!facebookAppId) return undefined;
    let cancelled = false;
    loadFacebookSdk()
      .then((FB) => {
        if (cancelled) return;
        FB.init({
          appId: facebookAppId, cookie: false, xfbml: false, version: GRAPH_VERSION,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [facebookAppId]);

  // Token client (not renderButton): error_callback fires with type popup_closed when
  // the user closes the Google window — GIS ID button has no cancel event (ORK-174 Retest 7).
  useEffect(() => {
    if (!googleClientId) {
      setGoogleReady(false);
      tokenClientRef.current = null;
      return undefined;
    }
    let cancelled = false;
    setGoogleReady(false);
    loadGoogleGsi()
      .then((oauth2) => {
        if (cancelled || !oauth2?.initTokenClient) return;
        tokenClientRef.current = oauth2.initTokenClient({
          client_id: googleClientId,
          scope: GOOGLE_SCOPES,
          callback: (response) => {
            const accessToken = response?.access_token;
            if (!accessToken) {
              setGoogleBusy(false);
              if (response?.error) {
                if (onErrorRef.current) onErrorRef.current('Google sign-in failed');
              } else {
                reportGoogleCancel();
              }
              return;
            }
            AuthService.signInWithGoogleAccessToken(accessToken)
              .then((me) => { if (onAuthenticatedRef.current) onAuthenticatedRef.current(me); })
              .catch((error) => {
                if (onErrorRef.current) onErrorRef.current(error?.message || 'Google sign-in failed');
              })
              .finally(() => setGoogleBusy(false));
          },
          error_callback: (err) => {
            setGoogleBusy(false);
            if (isPopupClosedError(err)) {
              reportGoogleCancel();
              return;
            }
            if (onErrorRef.current) {
              onErrorRef.current(err?.message || 'Google sign-in failed');
            }
          },
        });
        if (!cancelled) setGoogleReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setGoogleReady(false);
          tokenClientRef.current = null;
        }
      });
    return () => {
      cancelled = true;
      tokenClientRef.current = null;
    };
  }, [googleClientId]);

  function startGoogleLogin() {
    if (!googleClientId) {
      if (onError) onError('Google sign-in is not set up yet.');
      return;
    }
    const client = tokenClientRef.current;
    if (!googleReady || !client?.requestAccessToken) {
      if (onError) onError('Google is still loading. Try again.');
      return;
    }
    setGoogleBusy(true);
    // Must stay synchronous in the click handler so the popup keeps the user gesture.
    client.requestAccessToken({ prompt: 'select_account' });
  }

  function startFacebookLogin() {
    if (!facebookAppId) {
      if (onError) onError('Facebook sign-in is not set up yet.');
      return;
    }
    if (!window.FB) {
      if (onError) onError('Facebook is still loading. Try again.');
      return;
    }
    setFacebookBusy(true);
    window.FB.login((loginResponse) => {
      const accessToken = loginResponse?.authResponse?.accessToken;
      if (!accessToken) {
        setFacebookBusy(false);
        if (onError) onError('Facebook sign-in was cancelled.');
        return;
      }
      AuthService.signInWithFacebook(accessToken)
        .then((me) => { if (onAuthenticated) onAuthenticated(me); })
        .catch((error) => {
          if (onError) onError(error?.message || 'Facebook sign-in failed');
        })
        .finally(() => setFacebookBusy(false));
    }, { scope: FACEBOOK_SCOPE });
  }

  if (googleClientId === null && facebookAppId === null) return null;
  if (!googleClientId && !facebookAppId) return null;

  return (
    <Box sx={{ mt: 2, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      <Divider sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">Or continue with</Typography>
      </Divider>
      {googleClientId ? (
        <Button
          fullWidth
          variant="outlined"
          onClick={startGoogleLogin}
          disabled={googleBusy || !googleReady}
          startIcon={googleBusy ? <CircularProgress size={16} /> : <GoogleMarkIcon />}
          sx={{ mb: 1.5 }}
          data-testid="google-signin-button"
        >
          {googleBusy ? 'Connecting…' : 'Continue with Google'}
        </Button>
      ) : null}
      {facebookAppId ? (
        <Button
          fullWidth
          variant="outlined"
          onClick={startFacebookLogin}
          disabled={facebookBusy}
          startIcon={facebookBusy ? <CircularProgress size={16} /> : <FacebookIcon sx={{ color: '#1877F2' }} />}
        >
          {facebookBusy ? 'Connecting…' : 'Continue with Facebook'}
        </Button>
      ) : null}
    </Box>
  );
}

export default OAuthProviderButtons;
export {
  GOOGLE_CANCELLED_MESSAGE,
  GOOGLE_SCOPES,
  isPopupClosedError,
};
